import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";

const YEAR_OPTIONS = ["2024-2025", "2025-2026", "2026-2027", "2027-2028", "2028-2029"];

export function SemestersTab() {
  const [allYears, setAllYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("2025-2026");
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newYearDialog, setNewYearDialog] = useState(false);
  const [newYear, setNewYear] = useState("2026-2027");

  const fetchYears = async () => {
    try {
      const res = await api.get("/semesters/all-years");
      setAllYears(res.data.years || []);
      if (res.data.years?.length > 0 && !res.data.years.includes(selectedYear)) {
        setSelectedYear(res.data.years[0]);
      }
    } catch {}
  };

  const fetchSemesters = async (year) => {
    setLoading(true);
    try {
      const res = await api.get(`/semesters?academic_year=${year}`);
      setSemesters(res.data.semesters || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchYears(); }, []);
  useEffect(() => { fetchSemesters(selectedYear); }, [selectedYear]);

  const handleSave = async () => {
    try {
      await api.put("/semesters", { academic_year: selectedYear, semesters });
      toast.success(`Semester dates saved for ${selectedYear}`);
      fetchYears();
    } catch { toast.error("Failed to save semester dates"); }
  };

  const handleCreateNewYear = async () => {
    try {
      await api.post("/school-year/transition", { action: "create_new_year", to_year: newYear });
      toast.success(`School year ${newYear} created`);
      setNewYearDialog(false);
      await fetchYears();
      setSelectedYear(newYear);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create school year");
    }
  };

  const displayYears = [...new Set([...allYears, ...YEAR_OPTIONS])].sort().reverse();

  return (
    <div className="space-y-6">
      {/* School Year Selector */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>School Year Management</CardTitle>
              <CardDescription>Select a school year to configure semesters. Create new years for future planning.</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[180px]" data-testid="year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {displayYears.map(y => (
                    <SelectItem key={y} value={y}>
                      {y} {allYears.includes(y) ? "" : "(new)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => setNewYearDialog(true)} data-testid="create-year-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New Year
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Semester Dates */}
      <Card>
        <CardHeader>
          <CardTitle>Semester Dates — {selectedYear}</CardTitle>
          <CardDescription>Set start and end dates for each semester period.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-muted-foreground text-center py-4">Loading...</p>
          ) : (
            <>
              {semesters.map((sem, idx) => (
                <div key={sem.id} className={`p-4 rounded-lg border ${sem.id === 'summer' ? 'bg-amber-50 border-amber-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-[#00305E]">{sem.name}</h3>
                      {sem.is_optional && <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded">Optional</span>}
                    </div>
                    {sem.start_date && sem.end_date && (
                      <span className="text-xs text-muted-foreground">
                        {Math.ceil((new Date(sem.end_date) - new Date(sem.start_date)) / (1000 * 60 * 60 * 24))} days
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Start Date</Label>
                      <Input type="date" value={sem.start_date || ""} data-testid={`semester-${sem.id}-start`}
                        onChange={(e) => { const updated = [...semesters]; updated[idx] = { ...updated[idx], start_date: e.target.value }; setSemesters(updated); }} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">End Date</Label>
                      <Input type="date" value={sem.end_date || ""} data-testid={`semester-${sem.id}-end`}
                        onChange={(e) => { const updated = [...semesters]; updated[idx] = { ...updated[idx], end_date: e.target.value }; setSemesters(updated); }} />
                    </div>
                  </div>
                </div>
              ))}
              <Button onClick={handleSave} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-semesters-btn">Save Semester Dates</Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create New Year Dialog */}
      <Dialog open={newYearDialog} onOpenChange={setNewYearDialog}>
        <DialogContent className="max-w-sm" aria-describedby="new-year-desc">
          <DialogHeader>
            <DialogTitle>Create New School Year</DialogTitle>
            <p id="new-year-desc" className="text-sm text-muted-foreground">Set up semester dates for a new school year</p>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>School Year</Label>
              <Select value={newYear} onValueChange={setNewYear}>
                <SelectTrigger data-testid="new-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.filter(y => !allYears.includes(y)).map(y => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNewYearDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateNewYear} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="confirm-create-year-btn">Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
